import React from "react";
import { easyUpgrades, happyPlaceCategories, autumnNook, bestSellers } from "../mock";

export const SectionTitle = ({ children, subtitle, align = "center" }) => (
  <div className={`mb-8 ${align === "center" ? "text-center" : "text-left"}`}>
    <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--brand-ink)] tracking-tight">
      {children}
    </h2>
    {subtitle && (
      <p className="mt-2 text-sm text-neutral-600 max-w-lg mx-auto">{subtitle}</p>
    )}
  </div>
);

export const EasyUpgrades = ({ onAdd }) => (
  <section className="px-4 md:px-10 py-14 bg-white">
    <SectionTitle subtitle="Handpicked essentials at prices worth celebrating">Easy Upgrades, Perfect Prices</SectionTitle>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
      {easyUpgrades.map((item) => (
        <button
          key={item.id}
          onClick={() => onAdd?.(item)}
          className="group text-left"
        >
          <div className="zoom-wrap aspect-[4/5] bg-[color:var(--brand-cream)] rounded-sm">
            <img src={item.image} alt={item.label} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="pt-3">
            <div className="font-serif text-lg md:text-xl leading-tight group-hover:text-[color:var(--brand-red)] transition-colors">
              {item.label}
            </div>
            <div className="text-xs md:text-sm text-neutral-600 mt-1">{item.price}</div>
            <span className="inline-block mt-2 text-[11px] uppercase tracking-widest border-b border-[color:var(--brand-ink)] group-hover:border-[color:var(--brand-red)] group-hover:text-[color:var(--brand-red)] pb-0.5">
              Shop Now
            </span>
          </div>
        </button>
      ))}
    </div>
  </section>
);

const HAPPY_CAT_MAP = {
  "Sale": "All", "Furniture": "Furniture", "Food & Drink": "Kitchen",
  "Rugs": "Rugs", "Decor & Pillows": "Decor", "Lighting": "Lighting",
  "Wall Decor & Mirrors": "Decor", "Kitchen": "Kitchen", "Dining": "Dining", "Bath & Body": "Bedding",
};

export const HappyPlace = ({ onSelectCategory }) => (
  <section className="px-4 md:px-10 py-14 bg-[color:var(--brand-cream)]">
    <SectionTitle subtitle="Explore every corner of your home — curated by category">Create Your Happy Place</SectionTitle>
    <div className="max-w-7xl mx-auto grid grid-cols-3 md:grid-cols-5 gap-4 md:gap-6">
      {happyPlaceCategories.map((c) => (
        <button
          key={c.id}
          onClick={() => onSelectCategory?.(HAPPY_CAT_MAP[c.label] || "All")}
          className="flex flex-col items-center group"
        >
          <div className={`zoom-wrap w-full aspect-square rounded-full overflow-hidden ring-1 ring-neutral-200 ${c.pill ? "ring-[color:var(--brand-red)] ring-2" : ""}`}>
            <img src={c.image} alt={c.label} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className={`mt-3 text-[13px] md:text-sm font-medium tracking-wide group-hover:text-[color:var(--brand-red)] transition-colors ${c.pill ? "text-[color:var(--brand-red)]" : "text-neutral-800"}`}>
            {c.label}
          </div>
        </button>
      ))}
    </div>
  </section>
);

export const ThinPromoRow = ({ title, sub, cta, bg = "var(--brand-red)", color = "#fff", onClick }) => (
  <section style={{ background: bg, color }} className="px-6 py-6 md:py-8">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
      <div>
        <div className="font-serif text-xl md:text-2xl">{title}</div>
        {sub && <div className="text-sm opacity-90 mt-1">{sub}</div>}
      </div>
      <button onClick={onClick} className="bg-white text-[color:var(--brand-ink)] px-6 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-100 transition-colors">
        {cta}
      </button>
    </div>
  </section>
);

const AUTUMN_MAP = { "Wall Art": "Decor", "Rugs": "Rugs", "Pillows": "Decor", "Lighting": "Lighting", "Bloom Shop": "Decor", "Scent Studio": "Decor" };

export const AutumnNook = ({ onSelectCategory }) => (
  <section className="px-4 md:px-10 py-14 bg-white">
    <SectionTitle>Autumn in Every Nook</SectionTitle>
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
      {autumnNook.map((n) => (
        <button key={n.id} onClick={() => onSelectCategory?.(AUTUMN_MAP[n.label] || "All")} className="group block text-left">
          <div className="zoom-wrap aspect-[4/3] rounded-sm">
            <img src={n.image} alt={n.label} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="font-serif text-lg group-hover:text-[color:var(--brand-red)] transition-colors">{n.label}</div>
            <span className="text-[11px] uppercase tracking-widest border-b border-[color:var(--brand-ink)] group-hover:border-[color:var(--brand-red)] group-hover:text-[color:var(--brand-red)] pb-0.5">Shop</span>
          </div>
        </button>
      ))}
    </div>
  </section>
);

export const BestSellers = ({ onAdd }) => (
  <section className="px-4 md:px-10 py-14 bg-[color:var(--brand-cream)]">
    <SectionTitle subtitle="Our most-loved seating you’ll want to sink into">Best-Selling Seating</SectionTitle>
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
      {bestSellers.map((b) => (
        <button key={b.id} onClick={() => onAdd?.(b)} className="group text-left">
          <div className="zoom-wrap aspect-[4/5] rounded-sm bg-white">
            <img src={b.image} alt={b.label} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="pt-3">
            <div className="font-serif text-lg group-hover:text-[color:var(--brand-red)] transition-colors">{b.label}</div>
            <span className="inline-block mt-1 text-[11px] uppercase tracking-widest border-b border-[color:var(--brand-ink)] group-hover:border-[color:var(--brand-red)] group-hover:text-[color:var(--brand-red)] pb-0.5">Shop Collection</span>
          </div>
        </button>
      ))}
    </div>
  </section>
);

export const LargeSplitFeature = ({ eyebrow, title, body, cta, image, reverse, onClick }) => (
  <section className="bg-white">
    <div className={`max-w-7xl mx-auto flex flex-col ${reverse ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-6 md:gap-12 px-4 md:px-10 py-14`}>
      <div className="w-full md:w-1/2 zoom-wrap aspect-[4/3] md:aspect-[5/4] rounded-sm">
        <img src={image} alt={title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="w-full md:w-1/2 md:px-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-red)] mb-3">{eyebrow}</div>
        <h3 className="font-serif text-3xl md:text-4xl leading-tight">{title}</h3>
        <p className="mt-4 text-neutral-700 leading-relaxed max-w-md">{body}</p>
        <button onClick={onClick} className="mt-6 bg-[color:var(--brand-ink)] text-white px-7 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[color:var(--brand-red)] transition-colors">
          {cta}
        </button>
      </div>
    </div>
  </section>
);

export const TwoColumnPromo = ({ items, onClick }) => (
  <section className="px-4 md:px-10 py-14 bg-white">
    <SectionTitle>Reward Yourself</SectionTitle>
    <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
      {items.map((it, i) => (
        <div key={i} className="relative group overflow-hidden rounded-sm">
          <div className="zoom-wrap aspect-[16/9]">
            <img src={it.image} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/80">{it.badge}</div>
            <div className="font-serif text-2xl md:text-3xl mt-1">{it.title}</div>
            <p className="text-sm text-white/90 mt-2 max-w-md">{it.body}</p>
            <button
              onClick={() => onClick?.(it)}
              className="mt-4 bg-white text-[color:var(--brand-ink)] px-6 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-[color:var(--brand-red)] hover:text-white transition-colors"
            >
              {it.cta}
            </button>
          </div>
        </div>
      ))}
    </div>
  </section>
);
